// editor-model.tsx - Model Studio: cuboid-based 3D asset creator (Blockbench-style)
// Live R3F preview: orbit, solid/wireframe, per-element transforms and
// colours, over the same ModelElement document buildModelGroup exports from
// - the editing surface and the exported .glb finally share one renderer
// (docs/3d-content-pipeline/roadmap.md 3.1 Phase 1).
// Persists through the mode-document store as kind 'model' (see ModelDoc).
// Exports: ModelRoute
import { Edges, Grid, PerspectiveCamera } from '@react-three/drei';
import type { ThreeEvent } from '@react-three/fiber';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import type { DragEvent } from 'react';
import type { MeshStandardMaterial } from 'three';
import type { Brush } from 'three-bvh-csg';
import { exportModelGlb } from '../assets/modelGlbExport';
import { uploadGlbAsset } from '../assets/glbUpload';
import {
  createUnitModelGeometry, DEFAULT_MODEL_PROFILE, getObjectDefinition,
  MODEL_SHAPES, useMapEditorStore, Viewport, type ModelProfile, type ModelShape,
} from '../../engine';
// Imported from the concrete engine paths, not the published `../../engine`
// barrel: both pull in three-bvh-csg/three-mesh-bvh, which every published-
// library consumer would otherwise pay for even though only Model Studio's
// bake-time preview (this file) uses them.
import { evaluateModelCsg, hasCsgOps, MODEL_CSG_LIVE_ELEMENT_CAP, MODEL_CSG_OPS, type ModelCsgOp } from '../../engine/content/model/modelCsg';
import { MODEL_UNITS_PER_METRE } from '../../engine/content/model/modelGroup';
import { useProject } from '../project/ProjectProvider';
import { useEditorHost } from './EditorHost';
import { M2Label, M2Panel } from './editor-modes2';
import { PIXEL_PALETTES } from './editor-pixel';
import { inEditableTarget } from './useEditorShortcuts';
import { useDebouncedValue } from './useDebouncedValue';
import { useModeDocument } from './useModeDocument';

const DEG_TO_RAD = Math.PI / 180;

/** How long the live CSG boolean preview waits for edits to go quiet before
 * recomputing - short enough to still feel live, long enough that fast
 * typing or holding a number input's spin arrows collapses into one
 * recompute instead of one per keystroke/tick. */
const CSG_PREVIEW_DEBOUNCE_MS = 120;

/** Display labels for the shape picker, in `MODEL_SHAPES` order. */
const SHAPE_LABELS: Record<ModelShape, string> = {
  box: 'Box', sphere: 'Sphere', cylinder: 'Cylinder', cone: 'Cone', wedge: 'Wedge', extrude: 'Extrude',
};

/** Display labels for the boolean-op picker, in `MODEL_CSG_OPS` order. */
const CSG_OP_LABELS: Record<ModelCsgOp, string> = { add: 'Add', subtract: 'Subtract', intersect: 'Intersect' };

/** The 3D scene's selection-highlight accent, matching the dark-theme
 * `--ss-accent` this editor shell runs in (soulstack.css) - three.js
 * materials need a real colour, not a CSS custom property. */
const SELECTION_ACCENT = '#a78bfa';

export interface ModelElement {
  id: number;
  name: string;
  x: number;
  y: number;
  z: number;
  w: number;
  h: number;
  d: number;
  colour: string;
  /** Defaults to 'box' when unset (docs/3d-content-pipeline/roadmap.md
   * 3.1 Phase 1) - every element authored before richer primitives shipped
   * has no `shape` field and must keep rendering as the cuboid it always was. */
  shape?: ModelShape;
  /** Degrees, XYZ Euler order; rotates the element about its own centre. */
  rotation?: [number, number, number];
  /** Unset/'add' means solid (docs/3d-content-pipeline/roadmap.md 3.1
   * Phase 2) - see modelCsg.ts. */
  csgOp?: ModelCsgOp;
  /** Footprint polygon for `shape: 'extrude'` (3.1 Phase 3); ignored by
   * every other shape. */
  profile?: ModelProfile;
}

export const MODEL_START: ModelElement[] = [
  { id: 1, name: 'body', x: 0, y: 0, z: 0, w: 84, h: 56, d: 84, colour: '#3c3c6f' },
  { id: 2, name: 'lid', x: 0, y: 56, z: 0, w: 92, h: 16, d: 92, colour: '#2142d4' },
  { id: 3, name: 'handle', x: 0, y: 72, z: 0, w: 30, h: 8, d: 12, colour: '#bbe5fe' },
  { id: 4, name: 'foot_a', x: -32, y: -10, z: 32, w: 14, h: 10, d: 14, colour: '#222428' },
  { id: 5, name: 'foot_b', x: 32, y: -10, z: -32, w: 14, h: 10, d: 14, colour: '#222428' },
];

export interface MdlCuboidMeshProps {
  el: ModelElement;
  sel: boolean;
  wire: boolean;
  onSelect: (id: number) => void;
}

/** One authored primitive, rendered as a real mesh - the live editing
 * surface and `buildModelGroup`'s export bake share the same unit-geometry
 * factory (modelPrimitives.ts) and the same element shape (`x`/`z` centre
 * the element on the ground plane, `y` is its bottom face before rotation,
 * matching modelGroup.ts), so growing the primitive set here never lets the
 * two renderers drift apart. */
export function MdlCuboidMesh({ el, sel, wire, onSelect }: MdlCuboidMeshProps) {
  const geometry = useMemo(() => createUnitModelGeometry(el.shape ?? 'box', el.profile), [el.shape, el.profile]);
  // Passed to <mesh> as a prop (not declared as JSX children), so R3F never
  // takes ownership of it and never auto-disposes the outgoing geometry when
  // this memo recomputes on every shape/profile edit - dispose it ourselves.
  useEffect(() => () => geometry.dispose(), [geometry]);
  const rotation = el.rotation ?? [0, 0, 0];
  return (
    <mesh
      geometry={geometry}
      scale={[el.w, el.h, el.d]}
      position={[el.x, el.y + el.h / 2, el.z]}
      rotation={[rotation[0] * DEG_TO_RAD, rotation[1] * DEG_TO_RAD, rotation[2] * DEG_TO_RAD]}
      onClick={(e) => { e.stopPropagation(); onSelect(el.id); }}
    >
      <meshStandardMaterial color={el.colour} wireframe={wire} roughness={0.7} metalness={0.05} />
      {sel && <Edges color={SELECTION_ACCENT} threshold={1} />}
    </mesh>
  );
}

export interface CsgPreviewMeshProps {
  elements: ModelElement[];
  brush: Brush;
  selId: number | null;
  wire: boolean;
  onSelect: (id: number) => void;
}

/**
 * The live boolean-evaluated result (docs/3d-content-pipeline/roadmap.md
 * 3.1 Phase 2), rendered as a single mesh instead of one-per-element -
 * `evaluateModelCsg` tags each material with the source element's own array
 * index (`userData.modelElementIndex`), so clicking a triangle's own
 * `face.materialIndex` (three.js sets this for grouped multi-material
 * meshes, see Mesh.raycast) - which indexes the *output* material array,
 * reordered/compacted by the evaluator's own group consolidation, not
 * `elements` - resolves to the source element via that tag rather than by
 * position. There is no per-element outline to draw once the geometry is
 * merged into one buffer, so selection instead tints that element's own
 * material slot in place - still a clear "this one is selected" cue without
 * needing to isolate its triangles.
 */
function CsgPreviewMesh({ elements, brush, selId, wire, onSelect }: CsgPreviewMeshProps) {
  const materials = useMemo(() => (Array.isArray(brush.material) ? brush.material : [brush.material]) as MeshStandardMaterial[], [brush]);
  useEffect(() => {
    materials.forEach((mat) => {
      const el = elements[mat.userData.modelElementIndex as number];
      mat.wireframe = wire;
      const isSel = el?.id === selId;
      mat.emissive.set(isSel ? SELECTION_ACCENT : '#000000');
      mat.emissiveIntensity = isSel ? 0.6 : 0;
      mat.needsUpdate = true;
    });
  }, [materials, elements, wire, selId]);
  return (
    <primitive
      object={brush}
      onClick={(e: ThreeEvent<MouseEvent>) => {
        e.stopPropagation();
        const mat = materials[e.face?.materialIndex ?? -1];
        const el = mat ? elements[mat.userData.modelElementIndex as number] : undefined;
        if (el) onSelect(el.id);
      }}
    />
  );
}

export interface MdlNumProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
}

export function MdlNum({ label, value, onChange }: MdlNumProps) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 0 }}>
      <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.1em', color: 'var(--ss-text-3)', textTransform: 'uppercase' }}>{label}</span>
      <input className="ss-input" type="number" value={value}
        onChange={(e) => onChange(parseInt(e.target.value || '0', 10))}
        style={{ padding: '6px 8px', fontSize: 12.5, borderRadius: 7 }} />
    </label>
  );
}

/** The 'model' mode document: the authored cuboid model and view settings. */
export type ModelDoc = {
  els: ModelElement[];
  yaw: number;
  pitch: number;
  wire: boolean;
  /** The authored model name; saved assets are named after it. */
  name?: string;
};

const MODEL_DOC_FALLBACK: ModelDoc = { els: MODEL_START, yaw: 38, pitch: -22, wire: false, name: 'my_model' };

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

/**
 * Math.max(...[]) is -Infinity, so once every element is deleted the naive
 * `Math.max(...els.map(e => e.id)) + 1` collapses to -Infinity forever -
 * every subsequent "+ Add shape"/Duplicate then produces the same colliding
 * id: -Infinity, breaking React keys and selection in both the list and
 * the viewport.
 */
function nextElementId(els: ModelElement[]): number {
  return els.length === 0 ? 1 : Math.max(...els.map((e) => e.id)) + 1;
}

export function ModelRoute() {
  const [els, setEls] = useState<ModelElement[]>(MODEL_START);
  const [selId, setSelId] = useState<number | null>(1);
  const [yaw, setYaw] = useState(38);
  const [pitch, setPitch] = useState(-22);
  const [wire, setWire] = useState(false);
  const [name, setName] = useState('my_model');
  const sel = els.find((e) => e.id === selId);
  const { projectId } = useEditorHost();
  const { reload } = useProject();
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [saveError, setSaveError] = useState('');
  const importInputRef = useRef<HTMLInputElement>(null);

  async function saveToAssets() {
    if (saveState === 'saving') return;
    setSaveState('saving');
    setSaveError('');
    try {
      const buffer = await exportModelGlb(els);
      // The asset takes the authored name; a suffix keeps repeat saves distinct.
      const base = name.trim().toLowerCase().replace(/[^a-z0-9_-]+/g, '_').replace(/^_+|_+$/g, '') || 'model';
      const file = new File([buffer], `${base}.glb`, { type: 'model/gltf-binary' });
      await uploadGlbAsset(projectId, file);
      await reload();
      setSaveState('saved');
    } catch (err) {
      setSaveState('error');
      setSaveError(err instanceof Error ? err.message : 'Export failed.');
    }
  }

  async function importGlb(file: File) {
    setSaveState('saving');
    setSaveError('');
    try {
      await uploadGlbAsset(projectId, file);
      await reload();
      setSaveState('saved');
    } catch (err) {
      setSaveState('error');
      setSaveError(err instanceof Error ? err.message : 'Import failed.');
    }
  }

  // Persistence: hydrate once from the stored document, then debounce-save.
  const { data: storedDoc, setData: saveDoc, loading: docLoading } = useModeDocument<ModelDoc>('model', MODEL_DOC_FALLBACK);
  const hydrated = useRef(false);
  useEffect(() => {
    if (docLoading || hydrated.current) return;
    hydrated.current = true;
    setEls(storedDoc.els);
    setYaw(storedDoc.yaw);
    setPitch(storedDoc.pitch);
    setWire(storedDoc.wire);
    setName(storedDoc.name ?? 'my_model');
    setSelId(storedDoc.els[0]?.id ?? null);
  }, [docLoading, storedDoc]);
  useEffect(() => {
    if (docLoading || !hydrated.current) return;
    saveDoc({ els, yaw, pitch, wire, name });
  }, [els, yaw, pitch, wire, name, docLoading, saveDoc]);
  const palette = PIXEL_PALETTES[0]?.colours ?? ['#2142d4', '#71b4fe', '#3c3c6f', '#bbe5fe', '#988af6', '#222428'];
  // `els` itself stays live (so every number input stays a responsive,
  // controlled field) - only the expensive boolean evaluation below is
  // debounced, so rapid successive edits (fast typing, holding a spin
  // arrow) collapse into a single recompute once they pause, rather than
  // recomputing on every single change.
  const debouncedEls = useDebouncedValue(els, CSG_PREVIEW_DEBOUNCE_MS);
  // Only actually evaluate the boolean tree once a real op is in play, and
  // only up to Tinkercad's own documented ~30-shape live-preview cliff -
  // past that, per-frame CSG evaluation in an interactive editor stops
  // being a good trade (ops still apply correctly at export, which runs
  // once). See modelCsg.ts.
  const csgBrush = useMemo(() => {
    if (!hasCsgOps(debouncedEls) || debouncedEls.length > MODEL_CSG_LIVE_ELEMENT_CAP || debouncedEls.length === 0) return null;
    return evaluateModelCsg(debouncedEls);
  }, [debouncedEls]);
  // CsgPreviewMesh renders `brush` via `<primitive object={brush}>`, which
  // R3F never auto-disposes - nothing releases the outgoing brush's GPU
  // buffers when a live edit re-runs the boolean evaluation, so this has to
  // dispose the previous one explicitly.
  useEffect(() => () => {
    if (!csgBrush) return;
    csgBrush.geometry.dispose();
    const materials = Array.isArray(csgBrush.material) ? csgBrush.material : [csgBrush.material];
    for (const material of materials) material?.dispose();
  }, [csgBrush]);

  function patch(id: number, changes: Partial<ModelElement>) {
    setEls((all) => all.map((e) => (e.id === id ? { ...e, ...changes } : e)));
  }
  function addPrimitive(shape: ModelShape) {
    const id = nextElementId(els);
    const cube: ModelElement = {
      id, name: shape + '_' + id, x: 0, y: 90, z: 0, w: 30, h: 30, d: 30, colour: '#71b4fe', shape,
      profile: shape === 'extrude' ? DEFAULT_MODEL_PROFILE : undefined,
    };
    setEls([...els, cube]);
    setSelId(id);
  }

  // The bottom dock's "click to select for placement, or drag into viewport"
  // flow is store-driven (`pendingAssetId`), shared with Edit mode's
  // viewport - reading it here is what makes that flow actually do something
  // on this route instead of leaving a prefab stuck in a pending state with
  // no way to place it. A dock asset has no cuboid element of its own, so it
  // seeds one sized to the asset's registered bounds: a real (if rough)
  // starting block for that prop, not a no-op.
  const pendingAssetId = useMapEditorStore((state) => state.pendingAssetId);
  function placeFromAsset(assetId: string) {
    const definition = getObjectDefinition(assetId);
    if (!definition) return;
    const id = nextElementId(els);
    const [bw, bh, bd] = definition.bounds.size;
    const colour = typeof definition.defaultParams?.color === 'string' ? definition.defaultParams.color : (palette[0] ?? '#71b4fe');
    const h = Math.max(2, Math.round(bh * MODEL_UNITS_PER_METRE));
    const cube: ModelElement = {
      id,
      name: definition.id.replace(/[^a-z0-9]+/gi, '_'),
      x: 0,
      y: Math.round(h / 2),
      z: 0,
      w: Math.max(2, Math.round(bw * MODEL_UNITS_PER_METRE)),
      h,
      d: Math.max(2, Math.round(bd * MODEL_UNITS_PER_METRE)),
      colour,
    };
    setEls((all) => [...all, cube]);
    setSelId(id);
    useMapEditorStore.getState().setPendingAssetId(null);
  }
  function onStageDragOver(e: DragEvent<HTMLDivElement>) {
    if (e.dataTransfer.types.includes('text/plain')) e.preventDefault();
  }
  function onStageDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    const assetId = e.dataTransfer.getData('text/plain');
    if (assetId) placeFromAsset(assetId);
  }
  useEffect(() => {
    if (!pendingAssetId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== 'Escape' || e.repeat || inEditableTarget()) return;
      useMapEditorStore.getState().setPendingAssetId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pendingAssetId]);

  return (
    <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
      <M2Panel side="left" width={224}>
        <M2Label action={
          <select
            className="ss-input"
            value=""
            onChange={(e) => { const shape = e.target.value as ModelShape; if (shape) addPrimitive(shape); e.target.value = ''; }}
            style={{ appearance: 'none', padding: '3px 8px', fontSize: 11, borderRadius: 7 }}
            aria-label="Add primitive"
          >
            <option value="" disabled>+ Add shape</option>
            {MODEL_SHAPES.map((shape) => <option key={shape} value={shape}>{SHAPE_LABELS[shape]}</option>)}
          </select>
        }>Elements</M2Label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '0 8px' }}>
          {els.map((e) => (
            <button key={e.id} onClick={() => setSelId(e.id)} style={{
              display: 'flex', alignItems: 'center', gap: 9, padding: '7px 12px', borderRadius: 8, border: 'none', textAlign: 'left',
              background: selId === e.id ? 'var(--ss-accent-soft)' : 'transparent',
              color: selId === e.id ? 'var(--ss-accent)' : 'var(--ss-text-2)', fontSize: 12.5, fontWeight: 450,
            }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: e.colour, flexShrink: 0, border: '1px solid rgba(255,255,255,0.15)' }} />
              {e.name}
            </button>
          ))}
        </div>
        <M2Label>Import</M2Label>
        <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <input ref={importInputRef} type="file" accept=".glb" style={{ display: 'none' }}
            onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ''; if (file) void importGlb(file); }} />
          <button className="ss-btn ss-btn--ghost ss-btn--sm" style={{ justifyContent: 'center' }}
            disabled={saveState === 'saving'}
            onClick={() => importInputRef.current?.click()}>Import .glb</button>
          <span style={{ fontSize: 11, color: 'var(--ss-text-3)', lineHeight: 1.5 }}>Imported models go straight to your assets.</span>
        </div>
        <div style={{ marginTop: 'auto', padding: '14px 16px 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="ss-field-label">Model name</span>
            <input className="ss-input" value={name} onChange={(e) => setName(e.target.value)}
              style={{ padding: '7px 10px', fontSize: 12.5, borderRadius: 8 }} />
          </label>
          <button className="ss-btn ss-btn--primary ss-btn--sm" style={{ width: '100%', justifyContent: 'center' }}
            disabled={saveState === 'saving'}
            onClick={() => void saveToAssets()}>
            {saveState === 'saving' ? 'Saving' : saveState === 'saved' ? 'Saved to assets' : 'Save to assets'}
          </button>
          {saveState === 'error' && (
            <div style={{ marginTop: 8, fontSize: 11, color: 'var(--ss-danger)', lineHeight: 1.5 }}>{saveError}</div>
          )}
        </div>
      </M2Panel>

      {/* 3D stage */}
      <div
        onDragOver={onStageDragOver}
        onDrop={onStageDrop}
        style={{
          position: 'relative', flex: 1, minWidth: 0, overflow: 'hidden',
          background: 'radial-gradient(ellipse 70% 60% at 50% 40%, var(--ss-surface), var(--ss-bg-2) 80%)',
          cursor: pendingAssetId ? 'copy' : 'default',
          outline: pendingAssetId ? '1.5px solid var(--ss-accent)' : 'none',
          outlineOffset: -1.5,
        }}
      >
        <Viewport gl={{ antialias: true }}>
          <PerspectiveCamera makeDefault position={[0, 90, 340]} fov={38} />
          <color attach="background" args={['#0a0a0c']} />
          {/* onPointerMissed fires for a click that hits no mesh in the
              scene - the R3F equivalent of the old CSS stage's background
              onClick, which placed a pending dock asset when the click
              landed on empty space (GameRuntime.tsx uses this exact
              pattern for its own edit-mode placement-miss handling). */}
          <ambientLight intensity={0.6} onPointerMissed={() => { if (pendingAssetId) placeFromAsset(pendingAssetId); }} />
          <directionalLight position={[140, 240, 160]} intensity={1.3} />
          <group rotation={[(pitch * Math.PI) / 180, (yaw * Math.PI) / 180, 0]} position={[0, 20, 0]}>
            <Grid
              args={[400, 400]}
              cellSize={30}
              cellColor="#3a3f4a"
              sectionSize={90}
              sectionColor="#4a525c"
              fadeDistance={500}
              position={[0, -20, 0]}
            />
            {csgBrush ? (
              // `debouncedEls`, not the live `els` - `csgBrush`'s own
              // materials carry `userData.modelElementIndex` into the SAME
              // array it was built from (see evaluateModelCsg), which may
              // have already added/removed/reordered elements relative to
              // `els` during the debounce window.
              <CsgPreviewMesh elements={debouncedEls} brush={csgBrush} selId={selId} wire={wire} onSelect={setSelId} />
            ) : (
              els.map((e) => (
                <MdlCuboidMesh key={e.id} el={e} sel={selId === e.id} wire={wire} onSelect={setSelId} />
              ))
            )}
          </group>
        </Viewport>
        <span className="hm-chip" style={{ position: 'absolute', top: 14, left: 14, fontSize: 10.5, padding: '7px 14px', pointerEvents: 'none' }}>
          {(name.trim() || 'my_model')}.model · {els.length} elements{csgBrush ? ' · boolean preview' : ''}
        </span>
        {!csgBrush && hasCsgOps(els) && els.length > MODEL_CSG_LIVE_ELEMENT_CAP && (
          <span className="hm-chip" style={{ position: 'absolute', top: 14, right: 14, fontSize: 10.5, padding: '7px 14px', pointerEvents: 'none' }}>
            Too many elements for a live boolean preview ({MODEL_CSG_LIVE_ELEMENT_CAP}+) - ops still apply on export
          </span>
        )}
        {pendingAssetId && (
          <span className="hm-chip" style={{ position: 'absolute', top: 14, right: 14, fontSize: 10.5, padding: '7px 14px', color: 'var(--ss-accent)' }}>
            Click to place {pendingAssetId} · Escape to cancel
          </span>
        )}
        {/* view controls */}
        <div style={{
          position: 'absolute', bottom: 14, left: '50%', transform: 'translateX(-50%)',
          display: 'flex', alignItems: 'center', gap: 14, background: 'var(--ss-glass)', backdropFilter: 'blur(10px)',
          border: '1px solid var(--ss-border-soft)', borderRadius: 12, padding: '8px 14px',
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 10.5, color: 'var(--ss-text-3)' }}>
            Orbit
            <input type="range" min="0" max="360" value={yaw} onChange={(e) => setYaw(+e.target.value)} className="ed-range" style={{ width: 110 }} />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 10.5, color: 'var(--ss-text-3)' }}>
            Tilt
            <input type="range" min="-60" max="10" value={pitch} onChange={(e) => setPitch(+e.target.value)} className="ed-range" style={{ width: 70 }} />
          </label>
          <div style={{ display: 'flex', border: '1px solid var(--ss-border-soft)', borderRadius: 7, overflow: 'hidden' }}>
            {([['Solid', false], ['Wire', true]] as [string, boolean][]).map(([label, v]) => (
              <button key={label} onClick={() => setWire(v)} style={{
                border: 'none', padding: '4px 11px', fontSize: 9.5, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase',
                background: wire === v ? 'var(--ss-accent-soft)' : 'transparent',
                color: wire === v ? 'var(--ss-accent)' : 'var(--ss-text-3)',
              }}>{label}</button>
            ))}
          </div>
        </div>
      </div>

      <M2Panel side="right" width={252}>
        <M2Label>Element</M2Label>
        {sel ? (
          <Fragment>
            <div style={{ padding: '0 16px', marginBottom: 12 }}>
              <input className="ss-input" value={sel.name} onChange={(e) => patch(sel.id, { name: e.target.value })}
                style={{ padding: '8px 11px', fontSize: 12.5, borderRadius: 9 }} />
            </div>
            <M2Label>Position</M2Label>
            <div style={{ display: 'flex', gap: 6, padding: '0 16px', marginBottom: 10 }}>
              <MdlNum label="X" value={sel.x} onChange={(v) => patch(sel.id, { x: v })} />
              <MdlNum label="Y" value={sel.y} onChange={(v) => patch(sel.id, { y: v })} />
              <MdlNum label="Z" value={sel.z} onChange={(v) => patch(sel.id, { z: v })} />
            </div>
            <M2Label>Size</M2Label>
            <div style={{ display: 'flex', gap: 6, padding: '0 16px', marginBottom: 10 }}>
              <MdlNum label="W" value={sel.w} onChange={(v) => patch(sel.id, { w: Math.max(2, v) })} />
              <MdlNum label="H" value={sel.h} onChange={(v) => patch(sel.id, { h: Math.max(2, v) })} />
              <MdlNum label="D" value={sel.d} onChange={(v) => patch(sel.id, { d: Math.max(2, v) })} />
            </div>
            <M2Label>Rotation</M2Label>
            <div style={{ display: 'flex', gap: 6, padding: '0 16px', marginBottom: 10 }}>
              {(['x', 'y', 'z'] as const).map((axis, i) => (
                <MdlNum key={axis} label={axis.toUpperCase()} value={sel.rotation?.[i] ?? 0} onChange={(v) => {
                  const next: [number, number, number] = [...(sel.rotation ?? [0, 0, 0])];
                  next[i] = v;
                  patch(sel.id, { rotation: next });
                }} />
              ))}
            </div>
            <M2Label>Shape</M2Label>
            <div style={{ padding: '0 16px', marginBottom: 10 }}>
              <select className="ss-input" value={sel.shape ?? 'box'} onChange={(e) => {
                const shape = e.target.value as ModelShape;
                patch(sel.id, { shape, profile: shape === 'extrude' ? (sel.profile ?? DEFAULT_MODEL_PROFILE) : sel.profile });
              }} style={{ appearance: 'none', padding: '7px 10px', fontSize: 12.5, borderRadius: 8 }}>
                {MODEL_SHAPES.map((shape) => <option key={shape} value={shape}>{SHAPE_LABELS[shape]}</option>)}
              </select>
            </div>
            {sel.shape === 'extrude' && (
              <Fragment>
                <M2Label action={
                  <button className="ss-btn ss-btn--ghost" style={{ padding: '2px 8px', fontSize: 10.5, borderRadius: 6 }}
                    onClick={() => patch(sel.id, { profile: [...(sel.profile ?? DEFAULT_MODEL_PROFILE), [0, 0]] })}>+ Point</button>
                }>Profile (footprint)</M2Label>
                <div style={{ padding: '0 16px', marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {(sel.profile ?? DEFAULT_MODEL_PROFILE).map(([px, pz], i) => (
                    <div key={i} style={{ display: 'flex', gap: 4, alignItems: 'flex-end' }}>
                      <MdlNum label="X" value={px} onChange={(v) => {
                        const next = [...(sel.profile ?? DEFAULT_MODEL_PROFILE)] as ModelProfile;
                        next[i] = [v, next[i]![1]];
                        patch(sel.id, { profile: next });
                      }} />
                      <MdlNum label="Z" value={pz} onChange={(v) => {
                        const next = [...(sel.profile ?? DEFAULT_MODEL_PROFILE)] as ModelProfile;
                        next[i] = [next[i]![0], v];
                        patch(sel.id, { profile: next });
                      }} />
                      {(sel.profile ?? DEFAULT_MODEL_PROFILE).length > 3 && (
                        <button onClick={() => patch(sel.id, { profile: (sel.profile ?? DEFAULT_MODEL_PROFILE).filter((_, j) => j !== i) })}
                          title="Remove point" aria-label={'Remove point ' + (i + 1)}
                          style={{ border: 'none', background: 'transparent', padding: '6px 4px', fontSize: 13, color: 'var(--ss-text-3)' }}>×</button>
                      )}
                    </div>
                  ))}
                  <span style={{ fontSize: 10.5, color: 'var(--ss-text-3)', lineHeight: 1.5 }}>
                    Points trace the footprint in order; W/H/D above still scale the result.
                  </span>
                </div>
              </Fragment>
            )}
            <M2Label>Boolean op</M2Label>
            <div style={{ display: 'flex', border: '1px solid var(--ss-border-soft)', borderRadius: 7, overflow: 'hidden', margin: '0 16px 10px' }}>
              {MODEL_CSG_OPS.map((op) => (
                <button key={op} onClick={() => patch(sel.id, { csgOp: op })} style={{
                  flex: 1, border: 'none', padding: '5px 0', fontSize: 10, fontWeight: 600, letterSpacing: '0.04em',
                  background: (sel.csgOp ?? 'add') === op ? 'var(--ss-accent-soft)' : 'transparent',
                  color: (sel.csgOp ?? 'add') === op ? 'var(--ss-accent)' : 'var(--ss-text-3)',
                }}>{CSG_OP_LABELS[op]}</button>
              ))}
            </div>
            {els.length > 1 && (sel.csgOp ?? 'add') !== 'add' && els[0]?.id === sel.id && (
              <div style={{ padding: '0 16px 10px', fontSize: 10.5, color: 'var(--ss-text-3)', lineHeight: 1.5 }}>
                The first element in the list is always the base solid - its own op is ignored.
              </div>
            )}
            <M2Label>Colour</M2Label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 6, padding: '0 16px', marginBottom: 14 }}>
              {palette.map((c) => (
                <button key={c} onClick={() => patch(sel.id, { colour: c })} title={c} style={{
                  aspectRatio: '1', borderRadius: 7, background: c, cursor: 'pointer',
                  border: '2px solid ' + (sel.colour === c ? 'var(--ss-accent)' : 'rgba(255,255,255,0.1)'),
                }} />
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, padding: '0 16px' }}>
              <button className="ss-btn ss-btn--ghost" style={{ flex: 1, padding: '6px 0', fontSize: 11.5, borderRadius: 8 }}
                onClick={() => { const id = nextElementId(els); setEls([...els, { ...sel, id, name: sel.name + '_copy', x: sel.x + 20 }]); setSelId(id); }}>Duplicate</button>
              <button className="ss-btn ss-btn--ghost" style={{ flex: 1, padding: '6px 0', fontSize: 11.5, borderRadius: 8, color: 'var(--ss-danger)', borderColor: 'rgba(232,128,108,0.3)' }}
                onClick={() => { setEls(els.filter((e) => e.id !== sel.id)); setSelId(null); }}>Delete</button>
            </div>
            <div style={{ padding: '16px 16px 0', fontSize: 11, color: 'var(--ss-text-3)', lineHeight: 1.55 }}>
              Palettes come from Pixel Studio. Models export as GLB to your assets.
            </div>
          </Fragment>
        ) : (
          <div style={{ padding: '0 16px', fontSize: 12.5, color: 'var(--ss-text-3)' }}>Select an element in the list or viewport.</div>
        )}
      </M2Panel>
    </div>
  );
}
